import * as React from 'react'
import GibiLogo from '../../../assets/gibiLogo.svg'
import { checkImage } from '../../../helpers/petHelper.tsx'
import controlStore from '../../../store/controlStore.ts'
import dataStore from '../../../store/dataStore.ts'
import drawerStore from '../../../store/drawerStore.ts'
import mapStore from '../../../store/mapStore.ts'
import safeZoneStore from '../../../store/safeZoneStore.ts'
import { WebSocketIndicator } from '../../../webSockets/WebSocketIndicator.tsx'
import { CenterMapButton } from '../../common/buttons/CenterMapButton.tsx'
import { Dashboard2 } from '../../dashboard2/Dashboard2.tsx'
import { DesktopPetDrawerButton } from '../../petDrawer/DesktopPetDrawerButton.tsx'
import { MiniPetList } from '../../petDrawer/MiniPetList.tsx'
import { PetDrawer } from '../../petDrawer/PetDrawer.tsx'
import { NewSafeZone } from '../../safeZones/NewSafeZone.tsx'
import { SafeZones } from '../../safeZones/SafeZones.tsx'
import { POI_OFF } from '../components/GoogleMap/GoogleMapWrapper.tsx'
import { updatedStyles } from '../components/GoogleMap/mapStyles.ts'
import { Directions } from '../Directions/Directions.tsx'
import { MobileDirections } from '../Directions/MobileDirections.tsx'
import { LastKnownLocations } from '../LastKnownLocations/LastKnownLocations.tsx'

import '../map.css'

import { centerMapOnPets } from '../mapHelpers.tsx'
import { TrackMyLocation } from '../TrackMyLocation/TrackMyLocation.tsx'
import { ClearDirectionsButton } from '../Directions/ClearDirectionsButton.tsx'
import { MapTypeToggle } from '../../common/MapTypeToggle.tsx'

export const MainMap: React.FC = () => {
    // const google = mapStore((s) => s.google)
    const map = mapStore((s) => s.map)
    const isMobile = controlStore(s => s.isOnMobileDevice)
    const pets = dataStore(s => s.pets)
    const firstName = dataStore(s => s.account?.firstName)
    
    const mapStyle = mapStore(s => s.mapStyle)

    // Mount-only: set map context and current page. Cleanup runs on unmount only.
    React.useEffect(() => {
        mapStore.getState().setMapContext('map')
        controlStore.getState().setCurrentPage('map')
        drawerStore.getState().setDrawerOpen(false)
        return () => {
            drawerStore.getState().setDrawerOpen(false)
            safeZoneStore.getState().setActive(false)
        }
    }, [])

    // Keep the mobile pet-list drawer in sync with platform changes.
    React.useEffect(() => {
        drawerStore.getState().setChildren(isMobile ? <MiniPetList/> : null)
    }, [isMobile])

    // Prefetch pet images whenever the pet list changes.
    React.useEffect(() => {
        if (pets?.length) {
            void Promise.all(pets.map(p => checkImage(p.imageURL)))
        }
    }, [pets])
    
    // Apply map style when map loads or style changes. Center on style change only (not initial mount).
    const styleAppliedRef = React.useRef(false)
    React.useEffect(() => {
        if (map) {
            // @ts-ignore
            const styles = updatedStyles[mapStyle] ? [...(updatedStyles[mapStyle] as []), POI_OFF] : updatedStyles.default
            map.setOptions({styles})

            if (styleAppliedRef.current) {
                // Style changed after initial load — re-center
                const idleListener = map.addListener('idle', () => {
                    setTimeout(() => centerMapOnPets())
                    window.google.maps.event.removeListener(idleListener)
                })
            }
            styleAppliedRef.current = true
        }
    }, [map, mapStyle])
    
    const mobileHeader = (
        <div id={'mobile-header'}
             className={'w-full h-[64px] bg-white text-primary flex items-center justify-between px-2'}>
            <div><img src={GibiLogo} className={'h-[60px]'} alt='Gibi'/></div>
            <div className={'text-2xl font-bold'}> {firstName ? `Welcome ${firstName}` : ''} </div>
        </div>
    )
    
    return (
        <div id={'main-map'}
             className={'mainMap relative overflow-hidden border-transparent focus-visible:border-transparent focus-visible:ring-0'}
             style={{ height: '100%', width: '100%', pointerEvents: 'none' }}>
            {isMobile ? <div style={{ pointerEvents: 'auto' }}>{mobileHeader}</div> : null}
            <div style={{ pointerEvents: 'auto' }}>
                <TrackMyLocation/>
                <LastKnownLocations/>
                <SafeZones/>
                <NewSafeZone/>
                {isMobile ? <MobileDirections/> : <Directions/>}

                {!isMobile ? (
                    <div className={'absolute top-2 left-2'}>
                        <MapTypeToggle/>
                    </div>
                ) : (
                    <div className={'absolute top-[72px] left-2'}>
                        <MapTypeToggle/>
                    </div>
                )}

                <div className={'absolute top-2 left-[50%]'}>
                    <ClearDirectionsButton/>
                </div>

                <div className={'absolute bottom-8 left-4'}>
                    <WebSocketIndicator/>
                </div>
                <div className={`absolute ${!isMobile ? 'top-5 right-5' : 'top-20 right-3'}`}>
                    <div className={`flex flex-col justify-start items-center ${!isMobile ? 'gap-4' : 'gap-6'}`}>
                        {!isMobile ? <DesktopPetDrawerButton/> : null}
                        {/*<SafeZoneButton/>*/}
                        {/*<MapSettingsButton/>*/}
                        {/*<HelpButton/>*/}
                    </div>
                </div>
                <div id={'center-map-button'} className={`absolute ${!isMobile ? 'bottom-[8rem] right-4' : 'bottom-[9rem] right-5'}`}>
                    <CenterMapButton/>
                </div>
                {!isMobile ? (
                    <div id={'dash-2'} className={'absolute top-[10%] select-none'}>
                        <Dashboard2/>
                    </div>
                ) : (
                    <div className={'absolute bottom-0 z-10'}>
                        <PetDrawer/>
                    </div>
                )}
            </div>
        </div>
    )
}
